import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type KeyboardEvent, type MouseEvent, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/Store";
import {
  selectCameraPreview,
  selectCurrentEnvironmentId,
} from "../../app/slices/EnvironmentSlice";
import { setSelectedTake } from "../../app/slices/TrainingSlice";
import PiPopoverMenu from "../../components/PiPopoverMenu";
import { countDisabledInteractions } from "../../helpers/DisabledInteractionsHelper";
import { useCameraPreview } from "../../hooks/GetCameraPreviewHook";
import Take3D from "../../models/Take3D";
import DisabledInteractionsBadge from "../training_timeline/DisabledInteractionsBadge";
import { EditorColumnRow } from "../editor_columns/EditorColumn";
import PiMenuButton from "../menu/PiMenuButton";
import { TakeIssueSummary } from "./takeIssueSummary";
import { useDeleteTake3D } from "./useDeleteTake3D";
import {
  getTakeDndId,
  TakeInteractionDndData,
} from "../training_timeline/TakeInteractionDndProvider";

function getTakeFlag(take: Take3D, propertyId: string) {
  return Boolean(
    take.properties?.find((property) => property.id === propertyId)?.value,
  );
}

export function StoryboardTakeCardView(props: {
  take: Take3D;
  index: number;
  isSelected: boolean;
  isDropTarget?: boolean;
  isGhost?: boolean;
  issueSummary: TakeIssueSummary;
  cameraPreview?: string;
  onClick?: () => void;
  onDelete?: () => void;
  dragAttributes?: any;
  dragListeners?: any;
}) {
  const {
    take,
    index,
    isSelected,
    isDropTarget,
    isGhost,
    issueSummary,
    cameraPreview,
    onClick,
    onDelete,
    dragAttributes,
    dragListeners,
  } = props;
  const isStory = getTakeFlag(take, "isStory");

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    onClick?.();
  };

  const stopCardAction = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const thumbnail = (
    <div className="relative w-[44px] h-[44px] rounded-5 overflow-hidden bg-[var(--pi-timeline-interaction-bg)] border border-creator-border flex-shrink-0">
      {cameraPreview ? (
        <img
          src={cameraPreview}
          alt=""
          className="block object-cover w-full h-full"
        />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center text-creator-text-sub text-[18px]"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(0deg, rgba(0,0,0,0.06) 1px, transparent 1px)",
            backgroundSize: "12px 12px",
          }}
        >
          <MovieCreationOutlinedIcon fontSize="inherit" />
        </div>
      )}

      {issueSummary.hasErrors && (
        <span className="absolute bottom-0.5 right-0.5 inline-flex items-center gap-0.5 rounded-full px-1 py-0 text-[10px] font-semibold text-creator-error bg-creator-error-washed">
          <ErrorOutlineOutlinedIcon fontSize="inherit" />
          {issueSummary.errorCount}
        </span>
      )}
      {!issueSummary.hasErrors && issueSummary.hasWarnings && (
        <span className="absolute bottom-0.5 right-0.5 inline-flex items-center gap-0.5 rounded-full px-1 py-0 text-[10px] font-semibold text-[var(--pi-key-yellow)] bg-[var(--pi-key-yellow-faded)]">
          <WarningAmberOutlinedIcon fontSize="inherit" />
          {issueSummary.warningCount}
        </span>
      )}
    </div>
  );

  const menu = !isGhost && onDelete && (
    <span
      className="my-[-4px] flex-shrink-0"
      onClick={stopCardAction}
      onContextMenu={stopCardAction}
      onPointerDown={(event) => event.stopPropagation()}
      data-testid="storyboard-take-card-menu"
    >
      <PiPopoverMenu softIconHover modernMenu>
        <PiMenuButton
          text="Delete"
          icon={
            <DeleteOutlineOutlinedIcon color="inherit" fontSize="inherit" />
          }
          onClick={onDelete}
          variant="modern"
          tone="danger"
        />
      </PiPopoverMenu>
    </span>
  );

  return (
    <EditorColumnRow
      leading={
        <span className="flex items-center justify-center gap-2 text-creator-text-sub flex-shrink-0">
          <DragIndicatorIcon sx={{ fontSize: 14 }} />
          {thumbnail}
        </span>
      }
      title={take.name}
      titleClassName="font-normal"
      subtitle={
        isStory
          ? `Take ${(index + 1).toString().padStart(2, "0")} · Story`
          : `Take ${(index + 1).toString().padStart(2, "0")}`
      }
      trailing={
        <>
          <DisabledInteractionsBadge count={countDisabledInteractions(take)} />
          {menu}
        </>
      }
      selected={isSelected}
      dropTarget={isDropTarget}
      ghost={isGhost}
      defaultBorder
      onClick={onClick}
      onKeyDown={handleKeyDown}
      dragAttributes={dragAttributes}
      dragListeners={dragListeners}
      testId="storyboard-take-card"
      className="min-h-[58px]"
    />
  );
}

export default function StoryboardTakeCard3D(props: {
  take: Take3D;
  index: number;
  isSelected: boolean;
  isDropTarget?: boolean;
  issueSummary: TakeIssueSummary;
}) {
  const { take } = props;
  const dispatch = useDispatch();
  const { deleteTake3D } = useDeleteTake3D();
  const cameraId = take.mainCamera?.cameraId ?? "";
  const selectedEnvironment = useSelector(selectCurrentEnvironmentId);
  const [getCameraPreview] = useCameraPreview(cameraId);
  const cameraPreview = useSelector((state: RootState) =>
    selectCameraPreview(state, cameraId),
  );

  useEffect(() => {
    if (
      !cameraId ||
      !selectedEnvironment ||
      selectedEnvironment === "not-set" ||
      cameraPreview !== undefined
    ) {
      return;
    }

    getCameraPreview(selectedEnvironment);
  }, [cameraId, cameraPreview, getCameraPreview, selectedEnvironment]);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: getTakeDndId(take.id),
      data: { kind: "take", takeId: take.id } as TakeInteractionDndData,
      transition: {
        duration: 150,
        easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      },
    });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    width: "100%",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <StoryboardTakeCardView
        {...props}
        cameraPreview={cameraPreview}
        onClick={() => dispatch(setSelectedTake(take.id))}
        onDelete={() => deleteTake3D(take)}
        dragAttributes={attributes}
        dragListeners={listeners}
      />
    </div>
  );
}
